// hooks/useApiError.js
//
// One call site for every failure: show the plain sentence, send the raw detail
// to the error log, and sign the user out if their session has ended.
//
// Intended use in a page:
//
//     const handleError = useApiError();
//     try {
//         ...
//     } catch (err) {
//         handleError(err, { action: 'loadApplet' });
//     }
//
// Replaces the current pattern of `toast.error(err.message)`, which is how a
// browser's internal network wording reached a client's screen.
'use client';

import { useCallback, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import { fromAnything, fromApiResponse, fromHttpResponse } from '@/utils/apiError';
import { reportError, installFlushOnReconnect } from '@/utils/errorReporter';
import { notifySessionEnded } from '@/utils/sessionExpiry';

/**
 * @returns {(thing: any, context?: object) => object} handler; returns the
 *          normalised error so a caller can branch on it if it needs to.
 */
export default function useApiError() {
    const pathname = usePathname();
    const { authKey, currentOrg, user } = useAuth() || {};

    // Transport errors are created precisely when they cannot be delivered, so
    // anything queued gets another chance the moment the connection returns.
    useEffect(() => {
        return installFlushOnReconnect(() => ({
            authKey,
            orgId: currentOrg?.orgId,
        }));
    }, [authKey, currentOrg?.orgId]);

    return useCallback((thing, context = {}) => {
        const normalised = fromAnything(thing, context);

        // 1. Tell the user, in words they can act on — unless the session has
        //    ended, in which case the latched handler below owns the single
        //    message. Toasting here too would stack one per failed request.
        if (!normalised.sessionEnded) {
            toast.error(normalised.userMessage);
        }

        // 2. Send the detail somewhere useful. Never blocks, never throws.
        if (normalised.report) {
            reportError({
                ...normalised,
                route: pathname,
                orgId: currentOrg?.orgId,
                userId: user?.id,
                authKey,
            });
        }

        // 3. If the session is gone, hand it to the one latched handler rather
        //    than signing out from here. Several requests usually fail together
        //    when a key expires; acting locally gave one toast and one redirect
        //    per request. `AuthProvider` owns the actual sign-out.
        if (normalised.sessionEnded) {
            notifySessionEnded(normalised);
        }

        return normalised;
    }, [pathname, authKey, currentOrg?.orgId, user?.id]);
}

export { fromApiResponse, fromHttpResponse };
